// import '@adornis/';
import type { Styles } from '@adornis/ass/style.js';
import { ExtensionBoilerplate } from '@adornis/buildify/client/extensions/ContentExtensionBoilerplate.js';
import type { BuildifyExtension } from '@adornis/buildify/client/globals/types.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/digitale-helden-shared/client/components/component-badge-live.js';
import '@adornis/digitale-helden-shared/client/components/component-badge.js';
import '@adornis/digitale-helden-shared/client/components/component-icon-button.js';
import '@adornis/digitale-helden-shared/client/components/component-product-icon-text.js';
import '@adornis/digitale-helden-shared/client/formfields/d-product-picker.js';
import { Product } from '@adornis/digitale-helden-shared/db/product/Product.js';
import { ProductType } from '@adornis/digitale-helden-shared/db/product/enums.js';
import { getProductByID } from '@adornis/digitale-helden-shared/db/product/queries/getProductByID.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { filter, switchMap } from 'rxjs';
import '../../client/theme/d-text.js';
import { BaseProductTeaserCard } from '../db/BaseProductTeaserCard.js';

export const MarketingProductTeaserCard: BuildifyExtension = {
  class: BaseProductTeaserCard,
  toolbar: {
    icon: 'credit-card-front',
    text: {
      default: 'Produkt-Karte',
    },
  },
  group: 'Marketing',
  render(instance, { isSubelement, dropzoneBefore, dropzoneAfter }) {
    return html`
      <marketing-product-teaser-card
        .content=${instance}
        .defaultValues=${this.defaultValues}
        ?sub=${isSubelement}
        .hasDropzoneBefore=${dropzoneBefore}
        .hasDropzoneAfter=${dropzoneAfter}
      ></marketing-product-teaser-card>
    `;
  },
  editor({ content, contentController, controllerBaseKeyPath, host }) {
    return html`
      <d-flex space="sm">
        <d-product-picker ${contentController.field(...controllerBaseKeyPath, 'productID')}></d-product-picker>
      </d-flex>
    `;
  },
};

@customElement('marketing-product-teaser-card')
export class MProductTeaserCard extends ExtensionBoilerplate<BaseProductTeaserCard> {
  @state() private readonly _product = new RXController(
    this,
    this.content.observable.pipe(
      filter(Boolean),
      switchMap(content => getProductByID(content.productID)(Product.allFields)),
    ),
  );

  override _content(content: BaseProductTeaserCard) {
    const product = this._product.value;

    if (!product) {
      if (this.isEditMode) return html` <d-text> Kein Produkt verknüpft </d-text> `;
      return nothing;
    }

    return html`
      <d-flex>
        <!-- image -->
        ${this._productImage(product)}

        <!-- badges -->
        ${this._productBadges(product)}

        <!-- product icon text -->
        ${this._productIconText(product)}

        <!-- product name -->
        ${this._productName(product)}

        <!-- sales start -->
        ${this._productDate(product)}
      </d-flex>
    `;
  }

  protected _productImage(product: Product) {
    return html`
      <d-flex ${css({ position: 'relative' })}>
        <d-image
          disabled
          .value=${product.imageSmallUrl}
          mode="cover"
          ${css({
            borderRadius: '8px',
            overflow: 'hidden',
            width: '100%',
            aspectRatio: '16 / 9',
          })}
        >
        </d-image>
        <component-icon-button
          icon="arrow-up-right"
          tooltip="Zum Produkt"
          ${css({
            position: 'absolute',
            right: '0',
            bottom: '0',
            display: 'unset',
            padding: '8px 0 0 8px',
            background: '#fff',
            borderRadius: '8px 0 0 0',
          })}
        ></component-icon-button>
      </d-flex>
    `;
  }

  protected _productBadges(product: Product) {
    return html`
      <d-flex horizontal crossaxis-start space="sm" wrap ${css({ marginTop: '10px' })}>
        ${repeat(
          product.targetGroups ?? [],
          target => target,
          target => html`
            <component-badge
              .additionalTextStyle=${{ fontWeight: '700' }}
              icon="bullseye-arrow"
              text=${target}
              background=${this.colors.tone.lightGreen ?? ''}
              color=${this.colors.tone.darkGreen ?? ''}
            ></component-badge>
          `,
        )}
        ${product.type === ProductType.LIVE_WEBINAR ? html` <component-badge-live></component-badge-live> ` : nothing}
      </d-flex>
    `;
  }

  protected _productIconText(product: Product) {
    return html`
      <component-product-icon-text ${css({ marginTop: '28px' })} .product=${product}></component-product-icon-text>
    `;
  }

  protected _productName(product: Product) {
    return html` <d-text ${css({ marginTop: '8px' })} font-black color-dark-grey> ${product.name} </d-text> `;
  }

  protected _productDate(product: Product) {
    return html`
      <d-text color-grey ${css({ marginTop: '16px' })}>
        ${product.price ? 'kostenpflichtig' : 'kostenfrei'}${product.salesStartDate
          ? `, am
        ${product.salesStartDate.setLocale('DE-de').toLocaleString(DateTime.DATE_FULL)} ab
        ${product.salesStartDate.setLocale('DE-de').toLocaleString(DateTime.TIME_SIMPLE)} Uhr`
          : nothing}
      </d-text>
    `;
  }

  override styles() {
    return [
      ...super.styles(),
      {
        ':host': {
          width: '100%',
        },
      },
    ] as Styles[];
  }
}
